import { IntlShape } from 'react-intl';
import { LanguageCode } from '@osmosis/shared/languages';
import { PlaylistGroupProps } from '@osmosis/shared/types/playlist';
import { PlaylistItemProps } from '@osmosis/shared/types/playlist';
import { PlaylistProps } from '@osmosis/shared/types/playlist';
import { PlayListSort } from '@osmosis/shared/types/playlist';
import { PlaylistType } from '@osmosis/shared/types/playlist';
import { UserProps } from '@osmosis/shared/types/user';

import _groupBy from 'lodash/groupBy';
import { nameToLearnUrl } from '@osmosis/shared/video';

/**
 * Return learn page url.
 */
export const getUrl = (title: string, url: string) => {
  const fromData = encodeURIComponent(`/playlist/${url}`);

  return `${nameToLearnUrl(title)}?from=${fromData}`;
};

export const EMPTY_WATCH_LATER_LIST: PlaylistProps[] = [
  {
    progress: 0,
    type: PlaylistType.WATCH_LATER,
    title: 'Watch later',
    url: '',
    userID: null,
    userOwned: true,
    list: [],
    quizAllData: [],
    followers: [],
    created: '',
    modified: '',
    playlistID: -1,
    user: {
      preferredName: '',
      lastName: '',
      avatar: '',
    },
    media: {
      ques: {
        step1: {
          list: [],
        },
      },
      fc: [],
    },
  },
];

/**
 * Get translated title for playlist item.
 */
export const getTranslatedTitle = (
  playlistItem: PlaylistItemProps,
  lang: LanguageCode,
) => playlistItem.data.localizedTitles?.[lang] || playlistItem.title;

/**
 * Format playlist translations.
 */
export const formatGroups = (
  groups: PlaylistGroupProps[],
  user: UserProps | null,
): PlaylistGroupProps[] => {
  // Add watch later playlist if user is logged in
  if (user) {
    const watchLater = groups.find((g) => g.type === PlaylistType.WATCH_LATER);
    if (!watchLater) {
      groups.unshift({
        type: PlaylistType.WATCH_LATER,
        playlists: EMPTY_WATCH_LATER_LIST,
      });
    }

    return groups;
  }

  return groups;
};

/**
 * Maps playlist groups.
 */
export const formatePlaylistGroups = (
  playlistGroups: PlaylistGroupProps[],
  skipTypes: PlaylistType[] = [],
) => {
  let formattedPlaylistGroups = [...playlistGroups];

  const skipTypesSet = new Set(skipTypes);

  const includeWatchLater = !skipTypesSet.has(PlaylistType.WATCH_LATER);
  const includeOwned = !skipTypesSet.has(PlaylistType.USER_OWNED);
  const includeFollowing = !skipTypesSet.has(PlaylistType.FOLLOWING);
  const includeRecentlyWatched = !skipTypesSet.has(
    PlaylistType.RECENTLY_WATCHED,
  );

  const isWatchLaterExist = formattedPlaylistGroups.find(
    (playlist) => playlist.type === PlaylistType.WATCH_LATER,
  );
  const isUserOwnedExist = formattedPlaylistGroups.find(
    (playlist) => playlist.type === PlaylistType.USER_OWNED,
  );
  const isFollowedExists = formattedPlaylistGroups.find(
    (playlist) => playlist.type === PlaylistType.FOLLOWING,
  );
  const isRecentlyWatchedExisted = formattedPlaylistGroups.find(
    (playlist) => playlist.type === PlaylistType.RECENTLY_WATCHED,
  );
  if (!isUserOwnedExist && includeOwned) {
    formattedPlaylistGroups.push({ type: 1, playlists: [] });
  }
  if (!isFollowedExists && includeFollowing) {
    formattedPlaylistGroups.push({ type: 2, playlists: [] });
  }
  if (!isRecentlyWatchedExisted && includeRecentlyWatched) {
    formattedPlaylistGroups.push({ type: 3, playlists: [] });
  }

  if (isWatchLaterExist && includeWatchLater) {
    formattedPlaylistGroups = formattedPlaylistGroups.filter(
      (playlist) => playlist.type !== PlaylistType.WATCH_LATER,
    );
    if (isUserOwnedExist) {
      formattedPlaylistGroups = formattedPlaylistGroups.filter(
        (playlist) => playlist.type !== PlaylistType.USER_OWNED,
      );
    }
    formattedPlaylistGroups.unshift({
      type: PlaylistType.USER_OWNED,
      playlists: [
        ...isWatchLaterExist.playlists,
        ...(includeOwned ? isUserOwnedExist?.playlists || [] : []),
      ],
    });

    return formattedPlaylistGroups;
  }

  return formattedPlaylistGroups;
};
/**
 * Groups playlists by type.
 */
export const groupPlaylists = (
  playlists: PlaylistProps[],
): PlaylistGroupProps[] =>
  Object.entries(_groupBy(playlists, 'type')).map(([type, playlists]) => ({
    type: parseInt(type, 10),
    playlists,
  }));

/**
 * Filters Groups playlists by title.
 */
export const filterPlaylistsByTitle = (
  playlists: PlaylistGroupProps[],
  searchValue: string,
): PlaylistGroupProps[] =>
  playlists.map((playlistGroup) => {
    return {
      type: playlistGroup.type,
      playlists: [
        ...playlistGroup.playlists.filter((playlist) => {
          return playlist.title
            .toLowerCase()
            .includes(searchValue.toLowerCase());
        }),
      ],
    };
  });

/**
 * Sorts playlists by created date.
 */
export function sortByCreated(a: PlaylistProps, b: PlaylistProps) {
  const dateA = new Date(a.created);
  const dateB = new Date(b.created);

  return dateB.getTime() - dateA.getTime();
}

/**
 * Sorts playlists by last modified date.
 */
export function sortByModified(a: PlaylistProps, b: PlaylistProps) {
  const dateA = new Date(a.modified);
  const dateB = new Date(b.modified);

  return dateB.getTime() - dateA.getTime();
}

export const playListsSortMap: Record<string, string> = {
  [PlayListSort.RECENTLY_EDITED]: 'Recently edited',
  [PlayListSort.RECENTLY_CREATED]: 'Recently created',
  [PlayListSort.ALPHABETICAL_A_Z]: 'Alphabetical a-z',
};

/**
 * Sorts playlists by alphabetical order, created or last modified dates.
 */
export const sortPlaylists = (
  playlists: PlaylistGroupProps[],
  sortValue: string,
): PlaylistGroupProps[] =>
  playlists.map((playlistGroup) => {
    let sortedPlaylists = playlistGroup.playlists;
    switch (sortValue) {
      case PlayListSort.RECENTLY_CREATED:
        sortedPlaylists = sortedPlaylists.sort(sortByCreated);
        break;
      case PlayListSort.RECENTLY_EDITED:
        sortedPlaylists = sortedPlaylists.sort(sortByModified);
        break;
      case PlayListSort.ALPHABETICAL_A_Z:
        sortedPlaylists = sortedPlaylists.sort((a, b) =>
          a.title.localeCompare(b.title),
        );
        break;
      default:
        break;
    }

    return {
      type: playlistGroup.type,
      playlists: sortedPlaylists,
    };
  });

/**
 * Counts playlists.
 */
export const countPlaylists = (groups: PlaylistGroupProps[] = []): number => {
  const count = groups.reduce((acc, group) => {
    return acc + group.playlists.length;
  }, 0);

  return count;
};

/**
 * Filters playlist groups.
 */
export const getFirstPlaylist = (
  groups: PlaylistGroupProps[],
): PlaylistProps | null => {
  const playlist = groups.reduce((acc: PlaylistProps | null, group) => {
    return acc || group.playlists[0];
  }, null);

  return playlist;
};

/**
 * Finds playlist by URL.
 */
export const findPlaylistBy = (
  groups: PlaylistGroupProps[],
  filedValue: string | number,
  filedKey: 'url' | 'playlistID' = 'url',
): PlaylistProps | null => {
  const playlist = groups.reduce((acc: PlaylistProps | null, group) => {
    return (
      acc ||
      group.playlists.find((playlist) => playlist[filedKey] === filedValue) ||
      null
    );
  }, null);

  return playlist;
};

/**
 * Gets playlist group map.
 */
export const getIntlMap = (intl: IntlShape) => ({
  [PlaylistType.WATCH_LATER]: intl.formatMessage({
    id: 'playlist_group.watch_later',
    defaultMessage: 'Watch Later',
  }),
  [PlaylistType.USER_OWNED]: intl.formatMessage({
    id: 'playlist_group.user_owned',
    defaultMessage: 'My Playlists',
  }),
  [PlaylistType.FOLLOWING]: intl.formatMessage({
    id: 'playlist_group.following',
    defaultMessage: 'Following',
  }),
  [PlaylistType.RECENTLY_WATCHED]: intl.formatMessage({
    id: 'playlist_group.recently_watched',
    defaultMessage: 'Recently Watched',
  }),
});
